import { useCallback, useContext, useState } from 'react';
import { createContext } from 'react';

import { PORTFOLIO_TABS_NAME } from '../../constants/reports/reports-data';
import { DataParent, PortfolioManagementContext } from '../portfolio-management';
import usePersistentState from '../use-persistent-state';
import { WebSocketContext } from '../websocket-context/websocket-provider';

import {
  STRIKE_REPORT_FILTER_DISPLAY_INITIAL_STATE,
  STRIKE_REPORT_KEY,
} from './strike-report.constants';
import {
  IStrikeReportData,
  IStrikeReportDataParams,
  IStrikeReportFilters,
  IStrikeReportFilterToken,
  IStrikeReportParamsProvider,
  IStrikeReportProvider,
  IStrikeReportResponse,
  IStrikeReportResponseParams,
} from './strike-report.types';

export const StrikeReportContext = createContext<IStrikeReportProvider>(
  {} as IStrikeReportProvider,
);

export const STRIKE_REPORT_INITIAL_DATA: IStrikeReportData = {
  data: null,
  lastUpdate: '',
  isError: false,
};

export const StrikeReportProvider = (props: IStrikeReportParamsProvider) => {
  const { sendMessage, subscribeToChannel } = useContext(WebSocketContext);
  const { addDataToAccount, addGeneralAggregatedData } = useContext(PortfolioManagementContext);
  const [strikeReportData, setStrikeReportData] = useState<IStrikeReportData | null>(
    STRIKE_REPORT_INITIAL_DATA,
  );
  const [isStrikeReportLoading, setIsStrikeReportLoading] = useState(false);
  const [strikeReportExpandedRows, setStrikeReportExpandedRows] = useState<string[]>([]);
  const [strikeReportExpandedCols, setStrikeReportExpandedCols] = useState<string[]>([]);
  const [strikeReportFilterToken, setStrikeReportFilterToken] = useState<IStrikeReportFilterToken>({
    token: '',
    tokenList: [],
  });
  const [strikeReportFilterDisplay, setStrikeReportFilterDisplay] = useState<string[]>(
    STRIKE_REPORT_FILTER_DISPLAY_INITIAL_STATE,
  );
  const [sortStrikeAsc, setSortStrikeAsc] = usePersistentState<boolean>(
    ['STRIKE_REPORT'],
    'sortStrikeAsc',
    true,
  );

  const dataParent: DataParent = PORTFOLIO_TABS_NAME.strikeReport as DataParent;

  const strikeReportToggleRow = (id: string) => {
    setStrikeReportExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const strikeReportToggleColumn = (id: string) => {
    setStrikeReportExpandedCols((prev) =>
      prev.includes(id) ? prev.filter((colId) => colId !== id) : [...prev, id],
    );
  };

  const handleStrikeReportResponse = ({
    response,
    accounts,
    exchange,
  }: IStrikeReportResponseParams) => {
    if (!response) {
      setIsStrikeReportLoading(false);
      return;
    }

    const isStrikeReportResponse = response?.id === STRIKE_REPORT_KEY;
    if (!isStrikeReportResponse) {
      setIsStrikeReportLoading(false);
      return;
    }

    const isAggregatedAccounts = Array.isArray(accounts);
    const isError = Boolean(response.error);
    const data = !isError ? response.result : [];

    if (isAggregatedAccounts) {
      if (exchange) {
        addGeneralAggregatedData({
          dataParent,
          data,
          error: isError,
          exchange,
        });
      }
    } else {
      addDataToAccount({
        account: accounts,
        dataParent,
        data,
        error: isError,
      });
    }

    setIsStrikeReportLoading(false);
  };

  const getStrikeReportData = useCallback(({ accounts }: IStrikeReportDataParams) => {
    const accountList = Array.isArray(accounts) ? accounts : [accounts];

    const sources = accountList.map(({ exchange, venue, name }) =>
      exchange === 'otc' ? { venue, account: name } : { name },
    );

    const exchange = accountList[0].exchange;

    const payload = {
      id: STRIKE_REPORT_KEY,
      jsonrpc: '2.0',
      method: 'run_strike_report',
      params: {
        sources,
      },
    };

    setIsStrikeReportLoading(true);
    sendMessage(JSON.stringify(payload));
    subscribeToChannel(STRIKE_REPORT_KEY, (response: IStrikeReportResponse) =>
      handleStrikeReportResponse({ response, accounts, exchange }),
    );
  }, []);

  const strikeReportFilters: IStrikeReportFilters = {
    strikeReportFilterToken,
    setStrikeReportFilterToken,
    strikeReportFilterDisplay,
    setStrikeReportFilterDisplay,
  };

  return (
    <StrikeReportContext.Provider
      value={{
        isStrikeReportLoading,
        setIsStrikeReportLoading,
        strikeReportData,
        setStrikeReportData,
        strikeReportExpandedCols,
        strikeReportExpandedRows,
        strikeReportToggleColumn,
        strikeReportToggleRow,
        getStrikeReportData,
        strikeReportFilters,
        sortStrikeAsc,
        setSortStrikeAsc,
      }}
    >
      {props.children}
    </StrikeReportContext.Provider>
  );
};
